// Renders the in-app toast stack (see lib/toast.ts). Mounted once in AppShell.
//
// Pinned to the top of the viewport — centered on mobile, right-aligned on
// larger screens — above page content (z above the bottom nav, below the
// emulation banner's z-50 by using the same stacking but later in the DOM).
// The container is pointer-events-none so it never blocks clicks on the page;
// individual toasts re-enable pointer events for their dismiss button. The
// polite live region lets screen readers announce new messages without
// stealing focus.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { X } from 'lucide-react';

import { isAppPath } from '../lib/pushNotice';
import { subscribeToasts, dismissToast, type Toast, type ToastLink } from '../lib/toast';

// Power Blue, because following it is navigation (links stay Power Blue); the
// negative margin lines its label up with the text above.
const LINK_CLASS =
  '-mx-2 mt-1 inline-flex h-10 items-center rounded-lg px-2 text-sm font-bold text-powerBlue transition-colors duration-base hover:bg-powerBlue/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2';

/**
 * A toast's link. An app route goes through the router, so the page doesn't
 * reload; another site opens in a new tab, as it does from the notification
 * inbox. Following it dismisses the toast.
 */
function ToastLinkAction({ id, link }: { id: string; link: ToastLink }) {
  const dismiss = () => dismissToast(id);
  if (isAppPath(link.href)) {
    return (
      <Link to={link.href} onClick={dismiss} className={LINK_CLASS}>
        {link.label}
      </Link>
    );
  }
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={dismiss}
      className={LINK_CLASS}
    >
      {link.label}
    </a>
  );
}

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  // subscribeToasts syncs the current list immediately and returns the
  // unsubscribe function, which is exactly what useEffect wants back.
  useEffect(() => subscribeToasts(setToasts), []);

  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-0 z-50 flex flex-col items-center gap-2 px-4 pt-safe-3 sm:items-end"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className="pointer-events-auto w-full max-w-sm rounded-xl border border-cloud bg-white p-3 shadow-md animate-fade-in"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-carbon">{t.title}</p>
              {t.body && (
                <p className="mt-0.5 text-xs leading-normal text-steel">{t.body}</p>
              )}
              {t.link && <ToastLinkAction id={t.id} link={t.link} />}
            </div>
            <button
              type="button"
              onClick={() => dismissToast(t.id)}
              className="shrink-0 rounded-md p-0.5 text-steel transition-colors hover:bg-n-50 hover:text-carbon focus:outline-none focus:ring-2 focus:ring-orange"
              aria-label="Dismiss notification"
            >
              <X size={16} aria-hidden />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
